import { AsyncPipe } from '@angular/common';
import { Component, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { flowMapper, FlowTypes } from '@shared/models';
import { ContentPageId } from '@shared/models/content-page-ids';
import { LoggerCodes, LoggerService } from '@shared/modules/logger/logger';
import { WindowRef } from '@shared/modules/window-ref/window-ref';
import { rejectDAOffer } from '@store/actions/open-choice/open-choice.actions';
import * as fromRootStore from '@store/index';
import { ShowSurvey, TrackCallToAction, TrackProcessAbandon } from '@store/index';
import { selectRouterState } from '@store/selectors/router/common/router.selectors';
import { flowTypeSelection } from '@store/selectors/triage/triage.selectors';
import { ButtonComponent } from '@vg-constellation/angular-16/button';
import { ModalDialogComponent } from '@vg-constellation/angular-16/modal-dialog';
import * as _ from 'lodash';
import { withLatestFrom } from 'rxjs';

import { getConfigClientFromRTE } from '../../../core/config/config-client';
import { PageComponent } from '../page/page.component';

@Component({
  selector: 'app-cancel',
  templateUrl: './cancel.component.html',
  styleUrls: ['./cancel.component.scss'],
  standalone: true,
  imports: [RouterLink, ModalDialogComponent, ButtonComponent, AsyncPipe],
})
export class CancelComponent extends PageComponent implements OnInit {
  quitUrl = '';
  content: any;
  config: any;
  currentPage = '';
  isQuitModalOpen = false;
  currentFlow: FlowTypes;
  processStep: string;
  processType: string;

  @Input() isMultiSigner: boolean = false;
  @Input() isOpenChoice: boolean = false;
  @Input() quitButtonVariant: string = 'secondary';

  @ViewChild('quitModal') quitModal;

  @HostListener('window:message', ['$event'])
  onMessage(event: any): void {
    try {
      let dataObj = null;
      if ('data' in event) {
        dataObj = typeof event.data === 'object' ? event.data : JSON.parse(event.data);
      }
      if (!dataObj) return;

      if (
        dataObj.action === 'MDigital_Form_Close_Submitted' ||
        dataObj.action === 'MDigital_Form_Close_No_Submit' ||
        dataObj.action === 'MDigital_ThankYou_Close'
      ) {
        this.windowRef.nativeWindow.location.href = this.quitUrl;
      }
    } catch (e) {
      this.logger.error(
        { message: `Could not parse message data: ${e}` },
        LoggerCodes.WINDOW_MESSAGE_PARSE_FAILURE,
      );
    }
  }

  constructor(
    globalStore: Store<fromRootStore.State>,
    protected logger: LoggerService,
    private windowRef: WindowRef,
    private route: ActivatedRoute,
  ) {
    super(ContentPageId.cancel, globalStore);
  }

  ngOnInit() {
    super.ngOnInit();

    this.subs.add(
      this.content$.subscribe((content) => {
        this.content = content;
      }),
    );

    this.config = getConfigClientFromRTE();
    this.quitUrl = this.config.balances_url;

    this.subs.add(
      this.globalStore.select(selectRouterState).subscribe((routeData) => {
        _.attempt(() => {
          this.currentPage = routeData.url;
        });
      }),
    );

    this.subs.add(
      this.route.data
        .pipe(withLatestFrom(this.globalStore.select(flowTypeSelection)))
        .subscribe(([routeData, flowType]) => {
          this.currentFlow = flowType;
          const adobeFlowType = flowType ? flowMapper[flowType] : routeData.flow;
          this.processStep = `coo-${adobeFlowType}-${routeData.pageId}${this.isMultiSigner ? '-multi_signer' : ''}`;
          this.processType = `coo-${adobeFlowType}-flow`;
        }),
    );
  }

  openQuitModal() {
    this.adobeTrackQuitPopupShown();
    this.isQuitModalOpen = true;
    this.quitModal.openModalDialog();
  }

  closeQuitModalIcon() {
    this.adobeTrackAbortedQuit();
    this.isQuitModalOpen = false;
    this.quitModal.closeDialogModal();
  }

  closeQuitModalButton() {
    this.isQuitModalOpen = false;
    this.quitModal.closeDialogModal();
  }

  yesQuitButton() {
    _.attempt(() => {
      this.adobeTrackYesQuit();
      this.quitModal.closeDialogModal();
      if (this.isOpenChoice) {
        this.globalStore.dispatch(rejectDAOffer());
        this.globalStore.dispatch(fromRootStore.Go({ path: ['secure/minor-transfer/review'] }));
      } else {
        this.showSurvey();
      }
    });
  }

  adobeTrackQuitPopupShown() {
    this.globalStore.dispatch(
      TrackCallToAction({
        location: this.processStep,
        ctaName: 'coo-quit-popup-shown',
        ctaType: 'button',
      }),
    );
  }

  adobeTrackAbortedQuit() {
    this.globalStore.dispatch(
      TrackCallToAction({
        location: this.processStep,
        ctaName: 'coo-quit-aborted',
        ctaType: 'button',
      }),
    );
  }

  adobeTrackYesQuit() {
    this.globalStore.dispatch(
      TrackProcessAbandon({
        processType: this.processType,
        processStep: this.processStep,
        processAbandonmentType: 'quit',
      }),
    );
  }

  showSurvey() {
    this.globalStore.dispatch(
      ShowSurvey({
        payload: {
          survey_name: getInputQuitSurvey(this.currentFlow),
        },
      }),
    );
  }
}

function getInputQuitSurvey(flowType: FlowTypes) {
  switch (flowType) {
    case FlowTypes.MINOR:
      return 'quit_minor_inputpage';
    case FlowTypes.INTERNAL_TRANSFER:
      return 'quit_internal_transfer_inputpage';
    case FlowTypes.GIFTING:
      return 'quit_gifting_inputpage';
    case FlowTypes.INHERITANCE:
    default:
      return 'quit_inputpage';
  }
}
